import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useUserActivity } from "@/contexts/user-activity-context";
import { useQuery } from "@tanstack/react-query";
import {
  getUser,
  getUserActivity,
  LogResponse,
  PaginatedResponse,
} from "@/services/admin-services";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldAlert } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function UserActivitySheet() {
  const { isOpen, userId, closeUserActivity } = useUserActivity();

  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => (userId ? getUser(userId) : null),
    enabled: !!userId,
  });

  const { data: activityData, isLoading: isLoadingActivity } =
    useQuery<PaginatedResponse<LogResponse> | null>({
      queryKey: ["user-activity", userId],
      queryFn: () => (userId ? getUserActivity(userId) : null),
      enabled: !!userId,
    });

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeUserActivity()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-6">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-2xl">
            {isLoadingUser ? (
              <Skeleton className="h-8 w-[200px]" />
            ) : (
              userData?.email
            )}
          </SheetTitle>
          <SheetDescription>Recent activity log for this user</SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Route</TableHead>
                  <TableHead>Query Params</TableHead>
                  <TableHead>Request</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingActivity ? (
                  // Loading skeleton
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton className="h-4 w-[200px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[100px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[100px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[150px]" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : activityData?.data?.length ? (
                  activityData.data.map((activity) => (
                    <TableRow key={activity.id}>
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-2">
                          {activity.route}
                          {activity.impersonator_id && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <ShieldAlert className="h-4 w-4 text-blue-600" />
                                </TooltipTrigger>
                                <TooltipContent>Action performed by admin via impersonation</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {activity.query_params ? (
                          <pre className="text-xs">
                            {JSON.stringify(activity.query_params, null, 2)}
                          </pre>
                        ) : (
                          <span className="text-muted-foreground">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {activity.request ? (
                          <pre className="text-xs">
                            {JSON.stringify(activity.request, null, 2)}
                          </pre>
                        ) : (
                          <span className="text-muted-foreground">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            activity.method === "GET"
                              ? "bg-blue-100 text-blue-800"
                              : activity.method === "POST"
                              ? "bg-green-100 text-green-800"
                              : activity.method === "PUT"
                              ? "bg-yellow-100 text-yellow-800"
                              : activity.method === "DELETE"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {activity.method}
                        </span>
                      </TableCell>
                      <TableCell>
                        {format(activity.created_at, "MM/dd/yyyy hh:mm a zzz")}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center text-muted-foreground"
                    >
                      No activity found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
